/**
 * Iter269.20x — Reading-progress bar.
 *
 * A thin 3px horizontal bar pinned to the very top of the viewport
 * that fills (left → right) in QorTrace cyan as the visitor scrolls
 * down a long page. Auto-hides on short pages (where the document
 * is less than 1.5× the viewport tall — no point on `/scan` etc.).
 *
 * Pure CSS + a single passive scroll listener. Re-checks the
 * document height on resize so the "short page" gate stays accurate
 * as content lazy-loads (FAQ accordions, code-fold expansions etc.).
 *
 * Mounted inside <QorTraceOnlyChrome>, so /qorbom/* and /deck/*
 * surfaces are automatically pristine.
 */
import React, { useEffect, useRef, useState } from "react";

const SHORT_PAGE_RATIO = 1.5;   // hide when doc height < 1.5× viewport

export default function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);
  const [enabled,  setEnabled]  = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const recalcGate = () => {
      const docH = Math.max(
        document.body.scrollHeight, document.documentElement.scrollHeight,
        document.body.offsetHeight, document.documentElement.offsetHeight,
        document.body.clientHeight, document.documentElement.clientHeight,
      );
      const vh = window.innerHeight;
      setEnabled(docH > vh * SHORT_PAGE_RATIO);
    };

    const update = () => {
      rafRef.current = null;
      const docH = Math.max(
        document.body.scrollHeight, document.documentElement.scrollHeight,
      );
      const vh   = window.innerHeight;
      const span = Math.max(1, docH - vh);
      const pct  = Math.min(100, Math.max(0, (window.scrollY / span) * 100));
      setProgress(pct);
    };

    const onScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(update);
    };

    recalcGate();
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalcGate);
    // Re-check after route content settles (some pages load tall blocks
    // asynchronously — Methodology, audit reports, help-center markdown).
    const settleT = setTimeout(recalcGate, 750);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalcGate);
      clearTimeout(settleT);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Recheck the "short-page" gate every time the URL changes — without
  // this, navigating from a long page to a short one would leave the
  // bar mounted at 100%. Cheap belt-and-braces.
  useEffect(() => {
    const onNav = () => setTimeout(() => {
      const docH = Math.max(
        document.body.scrollHeight, document.documentElement.scrollHeight,
      );
      setEnabled(docH > window.innerHeight * SHORT_PAGE_RATIO);
      setProgress(0);
    }, 250);
    window.addEventListener("popstate", onNav);
    return () => window.removeEventListener("popstate", onNav);
  }, []);

  return (
    <div
      data-testid="reading-progress-bar"
      aria-hidden="true"
      style={{
        position:      "fixed",
        top:           0,
        left:          0,
        right:         0,
        height:        3,
        zIndex:        60,            // above the sticky nav (z-50)
        background:    "transparent",
        pointerEvents: "none",
        opacity:       enabled ? 1 : 0,
        transition:    "opacity 200ms ease",
      }}
    >
      <div
        style={{
          width:         `${progress}%`,
          height:        "100%",
          background:    "linear-gradient(90deg, var(--qt-cyan, #00E5FF) 0%, #9B7CFF 100%)",
          boxShadow:     "0 0 8px rgba(0,229,255,0.55)",
          transition:    "width 120ms linear",
          willChange:    "width",
        }}
      />
    </div>
  );
}
